/*
 * myPokemonDexCrossWrite.ts — owning a Pokémon proves the dex tick.
 *
 * A My Pokémon record IS evidence of a catch: a scanned Slaking means the
 * Slaking dex cell should be caught; an imported shiny row means the Shiny
 * Dex cell should be too. This pass walks the records and ticks the matching
 * Pokédex variants, so a 3,000-row Poké Genie import fills the trainer's dex
 * in the same breath (2026-08-20 user ask: "update the pokedex!").
 *
 * Deliberately one-directional and additive:
 *   - only ever marks PRESENT — never clears a dex cell (deleting a My
 *     Pokémon record says nothing about whether the species was caught),
 *   - roster-gated by isVariantAvailable, the same gate the dex grid uses,
 *     so a data-error row can't push a variant above its released roster
 *     (the exact overcount the scan path's unreleased-write guard exists
 *     for),
 *   - routed through bulkSetVariantPresent (the manual/deliberate path,
 *     NOT the scan-observation path), which already no-ops cells that are
 *     present, cascades Main from curated Shadow/Purified, and queues the
 *     server sync.
 */
import {
  bulkSetVariantPresent,
  getDexEntryState,
  setManual,
} from "./dexStateStore";
import { formDexCell } from "./formDexRoster";
import { isDexAutoUpdateEnabled } from "./dexAutoUpdatePref";
import { isVariantAvailable } from "./variantAvailability";
import type { PokedexVariant } from "./pogoPokedexVariantClassifier";
import { isPerfect, type MyPokemonRecord } from "./myPokemonStore";

export interface DexCrossWriteResult {
  /** Dex cells newly marked present, per variant (only non-zero entries). */
  byVariant: Partial<Record<PokedexVariant, number>>;
  /** Total cells newly marked. */
  total: number;
}

/** Which dex variants one record vouches for. */
function variantsFor(r: MyPokemonRecord): PokedexVariant[] {
  const out: PokedexVariant[] = ["pokemon"];
  if (r.isShiny) out.push("shiny");
  if (r.isLucky) out.push("lucky");
  if (r.isShadow) out.push("shadow");
  if (r.isPurified) out.push("purified");
  if (r.sizeClass === "xxl") out.push("xxl");
  if (r.sizeClass === "xxs") out.push("xxs");
  // The IV-derived dexes (2026-09-10): a scanned hundo vouches for 100%, and
  // the two combination dexes follow from the flags that make them up. These
  // were the three dexes a record could qualify for and never tick.
  if (isPerfect(r)) out.push("perfect");
  if (r.isShiny && isPerfect(r)) out.push("shundo");
  if (r.isShiny && r.isLucky) out.push("shinyLucky");
  // The Max/Mega axis (2026-09-02). Hand-set on the detail page, never scanned,
  // and each one is roster-gated below like every other variant — a Rattata
  // flagged Mega by a bad import still writes nothing.
  if (r.isMega) out.push("mega");
  if (r.isDynamax) out.push("dynamax");
  if (r.isGmax) out.push("gmax");
  // The Costume/Regional cell a record's formId vouches for is form-keyed, so
  // it cannot ride this one-write-per-variant path: see formCellsFor below.
  return out;
}

/**
 * The form dex a record's formId belongs to, or null (base form, a mechanical
 * form like Giratina Origin, or an id the species does not have). Costume is
 * asked first; the two rosters are disjoint.
 */
function formVariantFor(dexNumber: number, formId: string): PokedexVariant | null {
  if (formDexCell("costume", dexNumber, formId)) return "costume";
  if (formDexCell("regional", dexNumber, formId)) return "regional";
  return null;
}

/**
 * Tick the Costume / Regional cells the records' form ids vouch for
 * (2026-10-12, Nexus Dex CSV template). These are per-cell setManual writes,
 * each a whole-store read/write, which is why the bulk path above does not
 * carry them: but only rows that NAME a form reach here, de-duplicated by
 * cell and skipped when already present, so a 3,000-row import costs a
 * handful of writes rather than 3,000. Roster-gated by the registry itself:
 * an id the species does not have resolves to no variant and writes nothing.
 */
function writeFormCells(
  records: readonly MyPokemonRecord[],
  byVariant: Partial<Record<PokedexVariant, number>>,
): number {
  const seen = new Set<string>();
  let total = 0;
  for (const r of records) {
    if (r.speciesId == null || !r.formId) continue;
    const key = `${r.speciesId}:${r.formId}`;
    if (seen.has(key)) continue;
    seen.add(key);
    const variant = formVariantFor(r.speciesId, r.formId);
    if (!variant) continue;
    if (getDexEntryState(variant, r.speciesId, r.formId)?.state === "present") continue;
    if (!setManual(variant, r.speciesId, "present", r.formId)) continue;
    byVariant[variant] = (byVariant[variant] ?? 0) + 1;
    total += 1;
  }
  return total;
}

/**
 * Tick every dex cell these records vouch for. Additive, roster-gated, and
 * governed by the Settings switch (dexAutoUpdatePref): with "Update Pokédex
 * from scans" off, every call site no-ops at once.
 */
export function crossWriteMyPokemonToDex(
  records: readonly MyPokemonRecord[],
): DexCrossWriteResult {
  if (!isDexAutoUpdateEnabled()) return { byVariant: {}, total: 0 };
  const idsByVariant = new Map<PokedexVariant, Set<number>>();
  for (const r of records) {
    if (r.speciesId == null) continue;
    for (const variant of variantsFor(r)) {
      if (!isVariantAvailable(variant, r.speciesId)) continue;
      let ids = idsByVariant.get(variant);
      if (!ids) idsByVariant.set(variant, (ids = new Set()));
      ids.add(r.speciesId);
    }
  }
  const byVariant: Partial<Record<PokedexVariant, number>> = {};
  let total = 0;
  for (const [variant, ids] of idsByVariant) {
    const changed = bulkSetVariantPresent(variant, [...ids], true);
    if (changed > 0) {
      byVariant[variant] = changed;
      total += changed;
    }
  }
  total += writeFormCells(records, byVariant);
  return { byVariant, total };
}
